import { c as _c } from "react/compiler-runtime";
import { CurrencyEnum, PricePlanTypeEnum, SubscriptionIntervalEnum } from '@systemeio/types';
import { PricePlanCreateErrorsInterface, PricePlanCreateInterface } from 'modules/digital-product/price-plans/types/price-plan-interface';
import { getPricePlansTypesData, getSubscriptionIntervalData, isLimitedSubscription } from 'modules/digital-product/price-plans/utils/price-plans-utils';
import { TaxBehaviorEnum, taxBehaviorLocoKeys } from 'modules/payment/tax-behavior';
import React from 'react';
import { FieldErrorAndDescription, FormInput, FormInputNumber, FormInputNumberGroup, FormSelect, Hint, InputWithSelects, InputWithSelectsTrigger, RadioGroup, Select, type RadioGroupOption } from 'shared/components';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
export const defaultPricePlanCreateValues: PricePlanCreateInterface = {
  name: '',
  innerName: '',
  statementDescriptor: '',
  statementDescriptorKatakana: undefined,
  type: PricePlanTypeEnum.one_shot,
  amount: 0,
  taxBehavior: TaxBehaviorEnum.Exclusive,
  recurringOptions: {
    interval: SubscriptionIntervalEnum.day,
    intervalCount: 0,
    trialPeriod: 0,
    trialInterval: SubscriptionIntervalEnum.day,
    limitOfPayments: undefined
  }
};
export const defaultPricePlanCreateErrors: PricePlanCreateErrorsInterface = {
  name: '',
  innerName: '',
  statementDescriptor: '',
  type: '',
  amount: '',
  statementDescriptorKatakana: '',
  taxBehavior: '',
  recurringOptions: {
    interval: '',
    intervalCount: '',
    trialPeriod: '',
    trialInterval: '',
    limitOfPayments: ''
  }
};
interface PricePlanFormFieldsProps {
  currency: CurrencyEnum;
  errors: PricePlanCreateErrorsInterface;
  onChange: (update: (prev: PricePlanCreateInterface) => PricePlanCreateInterface) => void;
  value: PricePlanCreateInterface;
}

/**
 * Fields of a new price plan. Kept apart from the modal so the digital store can reuse them
 * once it moves onto the redesigned price plan flow.
 */
export function PricePlanFormFields(t0) {
  const $ = _c(132);
  const {
    currency,
    errors,
    onChange,
    value
  } = t0;
  const {
    t,
    tICU
  } = useLocoTranslation();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let taxBehaviorOptions;
  if ($[0] !== currency || $[1] !== errors.amount || $[2] !== errors.innerName || $[3] !== errors.name || $[4] !== errors.recurringOptions || $[5] !== errors.statementDescriptor || $[6] !== errors.statementDescriptorKatakana || $[7] !== errors.type || $[8] !== onChange || $[9] !== t || $[10] !== tICU || $[11] !== value.amount || $[12] !== value.innerName || $[13] !== value.name || $[14] !== value.recurringOptions?.interval || $[15] !== value.recurringOptions?.intervalCount || $[16] !== value.recurringOptions?.limitOfPayments || $[17] !== value.recurringOptions?.trialInterval || $[18] !== value.recurringOptions?.trialPeriod || $[19] !== value.statementDescriptor || $[20] !== value.statementDescriptorKatakana || $[21] !== value.type) {
    const trialIntervalData = getSubscriptionIntervalData(tICU, value.recurringOptions?.trialPeriod);
    const intervalData = getSubscriptionIntervalData(tICU, value.recurringOptions?.intervalCount);
    const isRecurring = value.type === PricePlanTypeEnum.subscription || value.type === PricePlanTypeEnum.limited_subscription;
    let t9;
    if ($[31] !== t) {
      t9 = t(taxBehaviorLocoKeys[TaxBehaviorEnum.Exclusive]);
      $[31] = t;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    let t10;
    if ($[33] !== t9) {
      t10 = {
        id: TaxBehaviorEnum.Exclusive,
        caption: t9
      };
      $[33] = t9;
      $[34] = t10;
    } else {
      t10 = $[34];
    }
    let t11;
    if ($[35] !== t) {
      t11 = t(taxBehaviorLocoKeys[TaxBehaviorEnum.Inclusive]);
      $[35] = t;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    let t12;
    if ($[37] !== t11) {
      t12 = {
        id: TaxBehaviorEnum.Inclusive,
        caption: t11
      };
      $[37] = t11;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    let t13;
    if ($[39] !== t10 || $[40] !== t12) {
      t13 = [t10, t12];
      $[39] = t10;
      $[40] = t12;
      $[41] = t13;
    } else {
      t13 = $[41];
    }
    taxBehaviorOptions = t13;
    let t14;
    if ($[42] !== onChange) {
      t14 = recurringOptions => onChange(prev => ({
        ...prev,
        recurringOptions: {
          ...prev.recurringOptions,
          ...recurringOptions
        }
      }));
      $[42] = onChange;
      $[43] = t14;
    } else {
      t14 = $[43];
    }
    const changeRecurringOptions = t14;
    t1 = "flex flex-col gap-4";
    let t15;
    if ($[44] !== t) {
      t15 = t("dashboard.funnel.configuration.common.price_plan.name_label");
      $[44] = t;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    let t16;
    if ($[46] !== t) {
      t16 = t("global.name");
      $[46] = t;
      $[47] = t16;
    } else {
      t16 = $[47];
    }
    let t17;
    if ($[48] !== onChange) {
      t17 = e => onChange(prev_0 => ({
        ...prev_0,
        name: e.target.value
      }));
      $[48] = onChange;
      $[49] = t17;
    } else {
      t17 = $[49];
    }
    if ($[50] !== errors.name || $[51] !== t15 || $[52] !== t16 || $[53] !== t17 || $[54] !== value.name) {
      t2 = <FormInput label={t15} placeholder={t16} value={value.name} onChange={t17} maxLength={256} error={errors.name} />;
      $[50] = errors.name;
      $[51] = t15;
      $[52] = t16;
      $[53] = t17;
      $[54] = value.name;
      $[55] = t2;
    } else {
      t2 = $[55];
    }
    let t18;
    if ($[56] !== t) {
      t18 = t("dashboard.funnel.configuration.common.price_plan.inner_name_label");
      $[56] = t;
      $[57] = t18;
    } else {
      t18 = $[57];
    }
    let t19;
    if ($[58] !== t) {
      t19 = t("global.name");
      $[58] = t;
      $[59] = t19;
    } else {
      t19 = $[59];
    }
    let t20;
    if ($[60] !== onChange) {
      t20 = e_0 => onChange(prev_1 => ({
        ...prev_1,
        innerName: e_0.target.value
      }));
      $[60] = onChange;
      $[61] = t20;
    } else {
      t20 = $[61];
    }
    if ($[62] !== errors.innerName || $[63] !== t18 || $[64] !== t19 || $[65] !== t20 || $[66] !== value.innerName) {
      t3 = <FormInput label={t18} placeholder={t19} value={value.innerName} onChange={t20} maxLength={256} error={errors.innerName} />;
      $[62] = errors.innerName;
      $[63] = t18;
      $[64] = t19;
      $[65] = t20;
      $[66] = value.innerName;
      $[67] = t3;
    } else {
      t3 = $[67];
    }
    let t21;
    if ($[68] !== t) {
      t21 = t("dashboard.funnel.configuration.common.price_plan.statement_descriptor_popover");
      $[68] = t;
      $[69] = t21;
    } else {
      t21 = $[69];
    }
    let t22;
    if ($[70] !== t) {
      t22 = t("dashboard.funnel.configuration.common.price_plan.statement_descriptor");
      $[70] = t;
      $[71] = t22;
    } else {
      t22 = $[71];
    }
    let t23;
    if ($[72] !== t21 || $[73] !== t22) {
      t23 = <Hint label={t21}>{t22}</Hint>;
      $[72] = t21;
      $[73] = t22;
      $[74] = t23;
    } else {
      t23 = $[74];
    }
    let t24;
    if ($[75] !== t) {
      t24 = t("dashboard.funnel.configuration.common.price_plan.statement_descriptor");
      $[75] = t;
      $[76] = t24;
    } else {
      t24 = $[76];
    }
    let t25;
    if ($[77] !== onChange) {
      t25 = e_1 => onChange(prev_2 => ({
        ...prev_2,
        statementDescriptor: e_1.target.value
      }));
      $[77] = onChange;
      $[78] = t25;
    } else {
      t25 = $[78];
    }
    if ($[79] !== errors.statementDescriptor || $[80] !== t23 || $[81] !== t24 || $[82] !== t25 || $[83] !== value.statementDescriptor) {
      t4 = <FormInput label={t23} placeholder={t24} value={value.statementDescriptor} onChange={t25} maxLength={22} error={errors.statementDescriptor} />;
      $[79] = errors.statementDescriptor;
      $[80] = t23;
      $[81] = t24;
      $[82] = t25;
      $[83] = value.statementDescriptor;
      $[84] = t4;
    } else {
      t4 = $[84];
    }
    if ($[85] !== currency || $[86] !== errors.statementDescriptorKatakana || $[87] !== onChange || $[88] !== t || $[89] !== value.statementDescriptorKatakana) {
      t5 = currency === CurrencyEnum.JAPANESE_YEN && <FormInput label={<Hint label={t("dashboard.funnel.configuration.common.price_plan.statement_descriptor_katakana_popover")}>{t("dashboard.funnel.configuration.common.price_plan.statement_descriptor_katakana")}</Hint>} placeholder={t("dashboard.funnel.configuration.common.price_plan.statement_descriptor_katakana")} value={value.statementDescriptorKatakana} onChange={e_2 => onChange(prev_3 => ({
        ...prev_3,
        statementDescriptorKatakana: e_2.target.value
      }))} maxLength={22} error={errors.statementDescriptorKatakana} />;
      $[85] = currency;
      $[86] = errors.statementDescriptorKatakana;
      $[87] = onChange;
      $[88] = t;
      $[89] = value.statementDescriptorKatakana;
      $[90] = t5;
    } else {
      t5 = $[90];
    }
    let t26;
    if ($[91] !== t) {
      t26 = t("dashboard.funnel.configuration.common.price_plan.type_label");
      $[91] = t;
      $[92] = t26;
    } else {
      t26 = $[92];
    }
    let t27;
    if ($[93] !== t) {
      t27 = getPricePlansTypesData(t);
      $[93] = t;
      $[94] = t27;
    } else {
      t27 = $[94];
    }
    let t28;
    if ($[95] !== onChange) {
      t28 = type => type && onChange(prev_4 => ({
        ...prev_4,
        type
      }));
      $[95] = onChange;
      $[96] = t28;
    } else {
      t28 = $[96];
    }
    if ($[97] !== errors.type || $[98] !== t26 || $[99] !== t27 || $[100] !== t28 || $[101] !== value.type) {
      t6 = <FormSelect withoutCloseIcon={true} label={t26} data={t27} value={value.type} onChange={t28} error={errors.type} />;
      $[97] = errors.type;
      $[98] = t26;
      $[99] = t27;
      $[100] = t28;
      $[101] = value.type;
      $[102] = t6;
    } else {
      t6 = $[102];
    }
    if ($[103] !== currency || $[104] !== errors.amount || $[105] !== onChange || $[106] !== t || $[107] !== value.amount || $[108] !== value.type) {
      t7 = value.type === PricePlanTypeEnum.one_shot && <FormInputNumberGroup label={t("dashboard.funnel.configuration.common.price_plan.direct_charge_amount")} className="max-w-[200px]" onChange={amount => onChange(prev_5 => ({
        ...prev_5,
        amount
      }))} suffix={currency} value={value.amount} error={errors.amount} />;
      $[103] = currency;
      $[104] = errors.amount;
      $[105] = onChange;
      $[106] = t;
      $[107] = value.amount;
      $[108] = value.type;
      $[109] = t7;
    } else {
      t7 = $[109];
    }
    t8 = isRecurring && <div className="tablet:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] tablet:gap-x-5 grid grid-cols-1 items-end gap-4"><InputWithSelects className="tablet:col-start-1" endSelect={<Select onChange={trialInterval => changeRecurringOptions({
        trialInterval
      })} value={value.recurringOptions?.trialInterval} data={trialIntervalData} trigger={<InputWithSelectsTrigger side="end">{trialIntervalData.find(option => Object.is(option.id, value.recurringOptions?.trialInterval))?.caption ?? t("global.select")}</InputWithSelectsTrigger>} />} input={<FormInputNumber className="min-w-0 flex-1" type="integer" label={t("dashboard.funnel.configuration.common.price_plan.subscription_plan.trial_period")} onChange={trialPeriod => changeRecurringOptions({
        trialPeriod
      })} value={value.recurringOptions?.trialPeriod ?? 0} maxLength={256} error={errors.recurringOptions.trialPeriod} />} /><FormInputNumberGroup label={t("dashboard.funnel.configuration.common.price_plan.subscription_plan.customers_will_pay")} className="tablet:col-start-1" onChange={amount_0 => onChange(prev_6 => ({
        ...prev_6,
        amount: amount_0
      }))} suffix={currency} value={value.amount} error={errors.amount} /><span className="mb-3">{t("global.each")}</span><InputWithSelects endSelect={<Select onChange={interval => changeRecurringOptions({
        interval
      })} value={value.recurringOptions?.interval} data={intervalData} trigger={<InputWithSelectsTrigger side="end">{intervalData.find(option_0 => Object.is(option_0.id, value.recurringOptions?.interval))?.caption ?? t("global.select")}</InputWithSelectsTrigger>} />} input={<FormInputNumber className="min-w-0 flex-1" type="integer" onChange={intervalCount => changeRecurringOptions({
        intervalCount
      })} value={value.recurringOptions?.intervalCount ?? 0} maxLength={256} error={errors.recurringOptions.intervalCount} />} />{isLimitedSubscription(value.type) && <FormInputNumber className="tablet:col-start-1" label={t("dashboard.funnel.configuration.common.price_plan.subscription_plan.limit_of_payments")} type="integer" onChange={limitOfPayments => changeRecurringOptions({
        limitOfPayments
      })} value={value.recurringOptions?.limitOfPayments ?? 0} maxLength={256} error={errors.recurringOptions.limitOfPayments} />}</div>;
    $[0] = currency;
    $[1] = errors.amount;
    $[2] = errors.innerName;
    $[3] = errors.name;
    $[4] = errors.recurringOptions;
    $[5] = errors.statementDescriptor;
    $[6] = errors.statementDescriptorKatakana;
    $[7] = errors.type;
    $[8] = onChange;
    $[9] = t;
    $[10] = tICU;
    $[11] = value.amount;
    $[12] = value.innerName;
    $[13] = value.name;
    $[14] = value.recurringOptions?.interval;
    $[15] = value.recurringOptions?.intervalCount;
    $[16] = value.recurringOptions?.limitOfPayments;
    $[17] = value.recurringOptions?.trialInterval;
    $[18] = value.recurringOptions?.trialPeriod;
    $[19] = value.statementDescriptor;
    $[20] = value.statementDescriptorKatakana;
    $[21] = value.type;
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = taxBehaviorOptions;
  } else {
    t1 = $[22];
    t2 = $[23];
    t3 = $[24];
    t4 = $[25];
    t5 = $[26];
    t6 = $[27];
    t7 = $[28];
    t8 = $[29];
    taxBehaviorOptions = $[30];
  }
  const t9 = value.taxBehavior;
  let t10;
  if ($[110] !== onChange) {
    t10 = taxBehavior => onChange(prev_7 => ({
      ...prev_7,
      taxBehavior
    }));
    $[110] = onChange;
    $[111] = t10;
  } else {
    t10 = $[111];
  }
  let t11;
  if ($[112] !== t) {
    t11 = t("dashboard.payment.tax_behavior.title");
    $[112] = t;
    $[113] = t11;
  } else {
    t11 = $[113];
  }
  let t12;
  if ($[114] !== t10 || $[115] !== t11 || $[116] !== taxBehaviorOptions || $[117] !== value.taxBehavior) {
    t12 = <RadioGroup options={taxBehaviorOptions} selected={t9} onChange={t10} label={t11} />;
    $[114] = t10;
    $[115] = t11;
    $[116] = taxBehaviorOptions;
    $[117] = value.taxBehavior;
    $[118] = t12;
  } else {
    t12 = $[118];
  }
  let t13;
  if ($[119] !== errors.taxBehavior) {
    t13 = <FieldErrorAndDescription error={errors.taxBehavior} />;
    $[119] = errors.taxBehavior;
    $[120] = t13;
  } else {
    t13 = $[120];
  }
  let t14;
  if ($[121] !== t1 || $[122] !== t12 || $[123] !== t13 || $[124] !== t2 || $[125] !== t3 || $[126] !== t4 || $[127] !== t5 || $[128] !== t6 || $[129] !== t7 || $[130] !== t8) {
    t14 = <div className={t1}>{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t12}{t13}</div>;
    $[121] = t1;
    $[122] = t12;
    $[123] = t13;
    $[124] = t2;
    $[125] = t3;
    $[126] = t4;
    $[127] = t5;
    $[128] = t6;
    $[129] = t7;
    $[130] = t8;
    $[131] = t14;
  } else {
    t14 = $[131];
  }
  return t14;
}